import { useCallback, useEffect, useRef } from "react";

/**
 * Creates a ref that always holds the current value it's passed,
 * but doesn't cause rerenders when that value changes.
 *
 * Helps intentionally address the react-hooks/exhaustive-deps eslint warning.
 *
 * Useful when you want to use an object or some other non-stable value
 * in a React hook, but don't want to include it in the dependencies.
 *
 * @param value The value to close on.
 * @returns
 */
export const useVolatileRef = <T>(value: T, deps?: any[]) => {
  const ref = useRef(value);

  useEffect(() => {
    ref.current = value;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, ...(deps ?? [])]);

  return ref;
};

/**
 * Creates a React callback that always calls the current value it's passed,
 * but doesn't cause rerenders when that value changes.
 *
 * Helps intentionally address the react-hooks/exhaustive-deps eslint warning.
 *
 * Useful when you want to have a react hook depend on a function that was passed
 * as a prop, but can't be counted on to be referentially stable.
 *
 * @param value The callback to close on.
 * @param deps An optional set of dependencies that should trigger a recreation
 * @returns
 */
export function useVolatileCallback<T extends (...args: any[]) => any>(
  value: T,
  deps?: any[],
): T;
export function useVolatileCallback<T extends (...args: any[]) => any>(
  value: T | undefined,
  deps?: any[],
): T | undefined;
export function useVolatileCallback<T extends (...args: any[]) => any>(
  value: T | undefined,
  deps?: any[],
) {
  const ref = useVolatileRef(value);

  const callback = useCallback<(...args: Parameters<T>) => ReturnType<T>>(
    (...args) => ref.current?.(...args),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ref, ...(deps ?? [])],
  );

  if (value === undefined) return undefined;
  return callback;
}
